import { useEffect } from 'react';

// Injects the Plausible script tag into <head> exactly once, only when
// VITE_PLAUSIBLE_DOMAIN is set at build time. Self-hosters can swap the
// CDN URL via VITE_PLAUSIBLE_SRC (e.g. 'https://plausible.example.com/js/script.js').
//
// Why a component and not a static <script> in index.html?
//   - We want the integration to be a no-op in dev / when the domain is
//     unconfigured — otherwise console gets a 404 noise for the script
//     and a CSP-violation report for plausible.io.
//   - Vite doesn't substitute env vars into index.html without a plugin.
//     A tiny component on mount is the simplest reliable path.
//
// The script is idempotent — Plausible's own loader guards against
// double-init, and we also guard with a data-attribute check below in
// case React StrictMode double-mounts during dev.

const SCRIPT_MARKER = 'data-plausible-injected';

export default function PlausibleAnalytics() {
  useEffect(() => {
    const domain = import.meta.env.VITE_PLAUSIBLE_DOMAIN;
    if (!domain) return;
    if (document.querySelector(`script[${SCRIPT_MARKER}]`)) return;

    const src = import.meta.env.VITE_PLAUSIBLE_SRC || 'https://plausible.io/js/script.js';

    const script = document.createElement('script');
    script.defer = true;
    script.setAttribute('data-domain', domain);
    script.setAttribute(SCRIPT_MARKER, 'true');
    script.src = src;
    document.head.appendChild(script);

    // Plausible's own helper for tracking custom events when the script
    // hasn't loaded yet: queue calls into a no-op until the real script
    // takes over. Without this, an early trackEvent() before the script
    // resolves would simply drop the event.
    if (typeof window.plausible !== 'function') {
      window.plausible = function plausibleQueue(...args) {
        (plausibleQueue.q = plausibleQueue.q || []).push(args);
      };
    }
  }, []);

  return null;
}
